import { useState, useRef } from "react";
import { Plus, Camera, User, Sliders, Key, KeyRound, LogOut, Laptop } from "lucide-react";
import { useOnboardingState } from "@/hooks/queries/use-onboarding";
import { adminList } from "@/components/onboarding/onboarding-guard";
import { PageInfoButton } from "@/components/common/page-info-button";
import { useAuthStore } from "@/stores/auth-store";
import { useTheme } from "@/hooks/use-theme";
import { usePageTitle } from "@/hooks/use-page-title";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNewLook } from "@/hooks/use-new-look";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTokens, useRevokeToken, useRotateToken } from "@/hooks/queries/use-tokens";
import { useUpdateUser } from "@/hooks/queries/use-users";
import { getInitials } from "@/lib/get-initials";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiKeyList } from "@/components/settings/api-key-list";
import { CreateApiKeyDialog } from "@/components/settings/create-api-key-dialog";
import { MachineList } from "@/components/machines/machine-list";
import { useBrandTheme } from "@/hooks/use-brand-theme";
import type { BrandTheme } from "@/hooks/use-brand-theme";
import { Link } from "react-router-dom";
import { ConnectComputerCard } from "@/components/machines/connect-computer";
import { AiSettingsCard } from "@/components/settings/ai-settings-card";

/** Common IANA timezone identifiers. Uses the runtime's full list when available. */
const TIMEZONE_OPTIONS: string[] =
  typeof (Intl as Record<string, unknown>).supportedValuesOf === "function"
    ? (Intl as unknown as { supportedValuesOf(key: string): string[] }).supportedValuesOf(
        "timeZone"
      )
    : [
        "UTC",
        "America/New_York",
        "America/Chicago",
        "America/Denver",
        "America/Los_Angeles",
        "America/Anchorage",
        "Pacific/Honolulu",
        "Europe/London",
        "Europe/Berlin",
        "Europe/Paris",
        "Asia/Tokyo",
        "Asia/Shanghai",
        "Asia/Kolkata",
        "Australia/Sydney",
      ];

const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const BRAND_THEME_OPTIONS: { value: BrandTheme; label: string; color: string }[] = [
  { value: "blue", label: "Blue", color: "#2563EB" },
  { value: "orange", label: "Orange", color: "#C16A2C" },
];

export function SettingsPage() {
  usePageTitle("Settings");
  const isMobile = useIsMobile();
  const [newLook, setNewLook] = useNewLook();
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useTheme();
  const { brand, setBrand } = useBrandTheme();
  const [name, setName] = useState(user?.name ?? "");
  const [timeZone, setTimeZone] = useState(user?.timeZone ?? browserTimeZone);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl ?? "");
  const [isCreateKeyDialogOpen, setIsCreateKeyDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: tokens, isLoading: isLoadingTokens } = useTokens();
  const revokeToken = useRevokeToken();
  const rotateToken = useRotateToken();
  const updateUser = useUpdateUser();
  const { data: onboarding } = useOnboardingState();
  const organization = onboarding?.step === "ready" && onboarding.organizationName ? onboarding : null;

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      alert("Image must be under 512 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setAvatarUrl(result);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = () => {
    if (!user) return;
    updateUser.mutate(
      { id: user.id, data: { name, timeZone, ...(avatarUrl ? { avatarUrl } : {}) } },
      {
        onSuccess: (res) => {
          // Merge with existing user to preserve fields not returned by PUT /users/:id
          // (e.g. role, teamId, isGlobalAdmin from membership join in /auth/me)
          const current = useAuthStore.getState().user;
          useAuthStore.setState({
            user: {
              ...(current ?? {}),
              ...res.data,
              // Preserve auth-critical fields from the existing store when available
              ...(current
                ? {
                    isGlobalAdmin: current.isGlobalAdmin,
                    role: current.role,
                    teamId: current.teamId,
                  }
                : {}),
            },
          });
        },
      }
    );
  };

  return (
    <div className="max-w-5xl mx-auto pt-4 px-4 md:px-0">
      {/* Header */}
      <div className={isMobile ? "mb-6 space-y-3" : "flex items-center justify-between mb-8"}>
        <div className="flex items-center gap-1">
          <h1 className="text-xl font-semibold">Settings</h1>
          <PageInfoButton title="Settings" />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground truncate">{user?.email}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              logout();
            }}
            className="text-muted-foreground hover:text-foreground gap-1.5"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </Button>
        </div>
      </div>

      <ConnectComputerCard />

      {/* Bento grid — single column on mobile */}
      <div className={isMobile ? "space-y-4" : "grid grid-cols-2 gap-4"}>
        {/* Account — spans left column */}
        <div
          className={`glass-card rounded-2xl border border-white/[0.08] p-5 md:p-6 ${isMobile ? "" : "row-span-2"}`}
        >
          <div className="flex items-center gap-2 mb-5">
            <User className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-medium">Account</h2>
          </div>

          <div className="flex flex-col items-center mb-6">
            <div className="relative group">
              <Avatar className="h-24 w-24">
                {avatarUrl && <AvatarImage src={avatarUrl} alt={name} />}
                <AvatarFallback className="text-2xl bg-primary/15 text-primary font-medium">
                  {getInitials(name || user?.name || "?")}
                </AvatarFallback>
              </Avatar>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <Camera className="h-5 w-5 text-white" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Click to change · Max 512 KB</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs text-muted-foreground">
                Display Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                className="bg-white/[0.04] border-white/[0.08]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                value={user?.email ?? ""}
                disabled
                className="bg-white/[0.04] border-white/[0.08] opacity-60"
              />
            </div>
            {organization && (
              <div className="space-y-1.5" data-testid="settings-organization">
                <p className="text-xs text-muted-foreground">Organization</p>
                <p className="px-3 text-sm">
                  {organization.organizationName}{" "}
                  <span className="text-muted-foreground">
                    · {organization.organizationRole === "admin" ? "Admin" : "Member"}
                  </span>
                </p>
                {organization.organizationRole !== "admin" && organization.admins.length > 0 && (
                  <p className="px-3 text-xs text-muted-foreground">
                    Admins: {adminList(organization.admins)}
                  </p>
                )}
              </div>
            )}
            <Button
              onClick={() => {
                handleSaveProfile();
              }}
              disabled={updateUser.isPending}
              size="sm"
              className="w-full"
            >
              {updateUser.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>

        {/* Preferences — top right */}
        <div className="glass-card rounded-2xl border border-white/[0.08] p-6">
          <div className="flex items-center gap-2 mb-5">
            <Sliders className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-medium">Preferences</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between opacity-50">
              <div>
                <div className="text-sm">Theme</div>
                <div className="text-xs text-muted-foreground">Appearance mode</div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    role="group"
                    aria-label="Theme selector — coming soon"
                    className="inline-block"
                  >
                    <Select value={theme} onValueChange={setTheme} disabled>
                      <SelectTrigger className="w-28 bg-white/[0.04] border-white/[0.08] cursor-not-allowed">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="light">Light</SelectItem>
                        <SelectItem value="dark">Dark</SelectItem>
                        <SelectItem value="system">System</SelectItem>
                      </SelectContent>
                    </Select>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Coming soon</TooltipContent>
              </Tooltip>
            </div>

            <div className="h-px bg-white/[0.06]" />

            <div className={isMobile ? "space-y-2" : "flex items-center justify-between"}>
              <div>
                <div className="text-sm">Brand theme</div>
                <div className="text-xs text-muted-foreground">Accent color</div>
              </div>
              <Select
                value={brand}
                onValueChange={(v) => {
                  setBrand(v as BrandTheme);
                }}
              >
                <SelectTrigger
                  aria-label="Brand theme selector"
                  className={`${isMobile ? "w-full" : "w-52"} bg-white/[0.04] border-white/[0.08]`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BRAND_THEME_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: opt.color }}
                        />
                        {opt.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="h-px bg-white/[0.06]" />

            <div className={isMobile ? "space-y-2" : "flex items-center justify-between"}>
              <div>
                <div className="text-sm">Timezone</div>
                <div className="text-xs text-muted-foreground">Dates &amp; activity</div>
              </div>
              <Select value={timeZone} onValueChange={setTimeZone}>
                <SelectTrigger
                  className={`${isMobile ? "w-full" : "w-52"} bg-white/[0.04] border-white/[0.08]`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz.replace(/_/g, " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="h-px bg-white/[0.06]" />

            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm" id="new-look-label">Try the new look</div>
                <div className="text-xs text-muted-foreground">
                  Preview of the redesigned CompBox. Switch back any time.
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={newLook}
                aria-labelledby="new-look-label"
                onClick={() => {
                  setNewLook(!newLook);
                }}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  newLook ? "bg-primary" : "bg-muted-foreground/30"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                    newLook ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* API Keys — bottom right */}
        <div className="glass-card rounded-2xl border border-white/[0.08] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-medium">API Keys</h2>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsCreateKeyDialogOpen(true);
              }}
              className="h-7 text-xs gap-1"
            >
              <Plus className="h-3 w-3" />
              New key
            </Button>
          </div>

          {isLoadingTokens ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
              Loading...
            </div>
          ) : (
            <>
              {tokens && tokens.length > 0 && (
                <div className="text-xs text-muted-foreground mb-3">
                  {tokens.length} {tokens.length === 1 ? "key" : "keys"}
                </div>
              )}
              <ApiKeyList
                tokens={tokens ?? []}
                onRevoke={(id) => {
                  revokeToken.mutate(id);
                }}
                isRevoking={revokeToken.isPending}
                onRotate={(id) => rotateToken.mutateAsync(id)}
                isRotating={rotateToken.isPending}
              />
            </>
          )}
        </div>

        {/* AI — how work runs: paired computer, own key, or the company key */}
        <div
          id="ai"
          className={`glass-card rounded-2xl border border-white/[0.08] p-6 ${isMobile ? "" : "col-span-2"}`}
        >
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-medium">AI</h2>
          </div>
          <AiSettingsCard />
        </div>

        <div className="glass-card rounded-2xl border border-white/[0.08] p-6 col-span-2">
          <p className="text-sm text-muted-foreground">
            GitHub, Cloud and Slack moved to{" "}
            <Link to="/connections" className="text-primary underline">
              Connections
            </Link>
            .
          </p>
        </div>

        {/* Paired Machines — spans full width below the bento grid */}
        <div
          id="computers"
          className={`glass-card rounded-2xl border border-white/[0.08] p-6 ${isMobile ? "" : "col-span-2"}`}
        >
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <Laptop className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-medium">Paired Machines</h2>
            </div>
            <Button asChild variant="ghost" size="sm" className="gap-1">
              <Link to="/connect">
                <Plus className="h-3.5 w-3.5" />
                Connect a computer
              </Link>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Machines paired with your account that can run agents from the browser.
            Rename them for clarity, or remove one to revoke its access.
          </p>
          <MachineList />
        </div>
      </div>

      <CreateApiKeyDialog open={isCreateKeyDialogOpen} onOpenChange={setIsCreateKeyDialogOpen} />

      <div className="text-center text-xs text-muted-foreground tracking-[0.2em] uppercase mt-12 pb-4">
        COMPBOX ©
      </div>
    </div>
  );
}
